import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import QRCode from "qrcode.react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Toolbar,
  Grid,
  InputLabel,
} from "@mui/material";
import { toast } from "react-toastify";
import mfaConfigImage from "../logos/Heading (2).png";
import image from "../logos/elmgrey.png";
import theme from "../theme";
import mainImage from "../logos/loginPageImage.png";
import { verifyMFAOTP } from "../utils/mfaUtils";
import { LoginRightSide } from "./Login";
import "./Login.css";
import { useTheme, useMediaQuery } from "@mui/material";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

const MFAHandler = ({
  email,
  loginToken,
  setupRequired,
  onSuccess,
  classes,
}) => {
  const [otp, setOtp] = useState("");
  const [qrData, setQrData] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [loading, setLoading] = useState(false);
  const api = localStorage.getItem("api");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (setupRequired) {
      setLoading(true);
      axios
        .post(
          `${api}/mfa/setup`,
          { email },
          {
            headers: {
              Authorization: `Bearer ${loginToken}`,
              "Content-Type": "application/json",
            },
          }
        )
        .then((res) => setQrData(res.data))
        .catch((error) =>
          toast.error("Failed to set up MFA", {
            position: toast.POSITION.BOTTOM_RIGHT,
          })
        )
        .finally(() => setLoading(false));
    }
  }, [setupRequired, email, api]);

  const verifyOTP = async () => {
    setVerifying(true);
    try {
      await verifyMFAOTP(email, otp, loginToken, onSuccess);
    } catch (err) {
      // Error is already handled in verifyMFAOTP utility function
    } finally {
      setVerifying(false);
    }
  };

  return (
    <>
      {setupRequired ? (
        <Box
          sx={{
            height: "100vh",
            overflow: "hidden",
            display: "flex",
          }}
        >
          <Box
            sx={{
              flex: { xs: "0 0 100%", md: "0 0 100%" },
              maxWidth: { xs: "100%", md: "100%" },
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              height: "100vh",
              background: "#fff",
              position: "relative",
              flexShrink: 0,
            }}
          >
            <Toolbar sx={{ backgroundColor: "#19433E", flexShrink: 0 }}>
              <Typography
                variant={"h6"}
                sx={{ color: "#fff" }}
              >
                Elhi Landscape Manager
              </Typography>
            </Toolbar>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "auto",
                py: 2,
              }}
            >
              <Box
                sx={{
                  width: { xs: "90%", sm: "50%" },
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  overflow: "visible",
                  borderRadius: 2,
                  border: "1px solid #19433E",
                  flexDirection: { xs: "column", sm: "row" },
                  py: 3,
                }}
                className="qr-login-main-box"
              >
                <Box
                  sx={{
                    width: { xs: "100%", sm: "50%" },
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    pr: { xs: 0, sm: 3 },
                    ml: { xs: 0, sm: -8 },
                    mb: { xs: 2, sm: 0 },
                  }}
                >
                  <Box
                    component="img"
                    src={mfaConfigImage}
                    alt="MFA Configuration"
                    sx={{
                      width: { xs: "50%", sm: "120%" },
                      height: "auto",
                      display: "block",
                    }}
                    className="qr-left-image"
                  />
                </Box>

                <Box
                  sx={{
                    width: { xs: "100%", sm: "50%" },
                    display: "flex",
                    justifyContent: { xs: "center", sm: "flex-start" },
                    pl: { xs: 0, sm: 2 },
                  }}
                >
                  <Box
                    sx={{
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      p: 2,
                      gap: 2,
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {!qrData ? (
                      <Typography>🔄 Loading QR Code...</Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 180,
                          maxWidth: "100%",
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            width: "100%",
                          }}
                        >
                          <QRCode
                            value={qrData.otpauth_url}
                            size={180}
                            level="L"
                            fgColor="#19433E"
                            renderAs="svg"
                            style={{
                              borderRadius: "4px",
                            }}
                          />
                        </Box>

                        <TextField
                          label="Enter OTP"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                          size="small"
                          variant="outlined"
                          sx={{ width: 180 }}
                        />

                        <Button
                          variant="contained"
                          color="primary"
                          onClick={verifyOTP}
                          disabled={verifying}
                          sx={{ textTransform: "none", width: 180 }}
                        >
                          Verify OTP
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: { xs: "flex", sm: "none" },
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                py: "8px",
                paddingBottom: "calc(8px + env(safe-area-inset-bottom))",
                backgroundColor: "transparent",
                pointerEvents: "none",
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  color: "rgb(20,38,40)",
                  fontSize: { xs: "7.5px", sm: "7.5px" },
                  lineHeight: 1.15,
                  letterSpacing: "-0.5px",
                  fontFamily: "'Open Sans', sans-serif",
                  textAlign: "center",
                  px: 1,
                  fontWeight: 400,
                }}
              >
                {`v1.0.1 • Copyright ${new Date().getFullYear()} • Powered by ElhiTech • support@elhi.tech`}
              </Typography>
            </Box>

            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                justifyContent: "center",
                alignItems: "center",
                pb: "2%",
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  color: "rgb(20,38,40)",
                  fontSize: { xs: "7.5px", sm: "0.67vw" },
                  lineHeight: 1.15,
                  letterSpacing: "-0.5px",
                  fontFamily: "'Open Sans', sans-serif",
                  textAlign: "center",
                  fontWeight: 400,
                }}
                className="desktop-copyright"
              >
                {`v1.0.1 • Copyright ${new Date().getFullYear()} • Powered by ElhiTech • support@elhi.tech`}
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            flexDirection: { xs: "column-reverse", md: "row" },
            overflow: "hidden",
            flexWrap: "wrap-reverse",
          }}
        >
          <Box
            sx={{
              height: { xs: "75%", md: "100%" },
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              width: {
                xs: "100%",
                md: "35.75vw",
                ld: "35.25vw",
              },
              maxWidth: { xs: "unset", md: "645px" },
              background: "rgb(255,255,255)",
              pl: { xs: "10%", md: "7.35vw" },
              pr: { xs: "10%", md: "7.35vw" },
              pt: { xs: "1.5%", md: "8.9vh" },
              pb: { xs: "7.5%", md: "5%" },
              position: "relative",
              zIndex: 200,
            }}
          >
            {/* Logo — desktop only */}
            <Box
              sx={{
                width: "100%",
                display: { xs: "none", md: "block" },
              }}
            >
              <img
                alt="Elhi Landscape Manager"
                src="https://storage.googleapis.com/content_ai_bucket_elhitech/content/d5663d2c-2f7e-40a0-b219-aee4dd2b9613-02c26c3a6208f366a2973bbef697bcda33f6de96%20(1)%20(1).png"
                className="desktop-elhi-logo"
                style={{
                  height: "auto",
                  display: "block",
                  marginLeft: 0,
                  marginBottom: "16px",
                }}
              />
            </Box>

            {/* Body — same top margin as login form area */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                marginTop: isMobile ? "20px" : "2.9vh",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: "rgb(25,67,62)",
                  textAlign: "start",
                  fontWeight: "bold",
                  fontSize: { xs: "1.5rem", md: "2.9vh" },
                  marginLeft: { xs: 0, md: "0.2vw" },
                  marginBottom: "5px",
                }}
              >
                2FA Authentication
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "primary.main",
                  mb: { xs: "26px", md: "3.1vh" },
                  ml: { xs: 0, md: "0.2vw" },
                  textAlign: "start",
                  fontFamily: "Open Sans, sans-serif",
                  fontSize: { xs: "11px", md: "0.76vw" },
                  whiteSpace: "nowrap",
                  overflow: "visible",
                  textOverflow: "clip",
                  width: "max-content",
                  maxWidth: "none",
                  display: "block",
                }}
                className="request-access-text"
              >
                Please enter the MFA token from your Authenticator App
              </Typography>

              {/* OTP field — full width like login fields */}
              <Box
                sx={{
                  pl: { xs: 0, md: "0.2vw" },
                  pr: { xs: 0, md: "0.6vw" },
                  display: "flex",
                  flexDirection: "column",
                  gap: { xs: 0, md: "1vh" },
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    marginBottom: "10px",
                  }}
                >
                  <InputLabel
                    style={{
                      color: "rgb(25,67,62)",
                      textAlign: "start",
                      marginBottom: "3px",
                      fontWeight: "bold",
                      fontSize: isMobile ? "13px" : "0.76vw",
                      fontFamily: "Open Sans, sans-serif",
                    }}
                  >
                    MFA Token
                  </InputLabel>




                  <TextField
                    variant="outlined"
                    type="text"
                    size="small"
                    placeholder="******"
                    required
                    fullWidth
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    InputProps={{
                      style: {
                        borderRadius: "8px",
                        background: "rgb(230, 233, 236)",
                      },
                    }}
                    inputProps={{
                      style: {
                        height: isMobile ? "1.65em" : "2.3vh",
                      },
                    }}
                  />
                </div>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={verifyOTP}
                  disabled={verifying}
                  fullWidth
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    height: isMobile ? "3.6em" : "2.22vw",
                    fontSize: isMobile ? "calc(3.6em * 0.32)" : "calc(2.4vw * 0.36)",
                    minHeight: 0,
                    padding: 0,
                    maxHeight: "38px",
                  }}
                  className="submit-btn"
                >
                  Verify and Sign In <ArrowRightAltIcon
                   
                  />
                </Button>
              </Box>
            </Box>

            {/* Copyright — mobile (fixed) */}
            <Box
              sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 9999,
                display: { xs: "flex", md: "none" },
                justifyContent: "center",
                alignItems: "center",
                width: "100vw",
                paddingBottom: "env(safe-area-inset-bottom)",
                py: "8px",
                backgroundColor: "transparent",
                pointerEvents: "none",
              }}
            >
              <Typography
                sx={{
                  color: "rgb(20,38,40)",
                  fontSize: { xs: "7.5px", sm: "7.5px" },
                  lineHeight: 1.15,
                  fontWeight: 400,
                  letterSpacing: "-0.5px",
                  fontFamily: "'Open Sans', sans-serif",
                  textAlign: "center",
                  px: 1,
                }}
              >
                {`v1.0.1 • Copyright ${new Date().getFullYear()} • Powered by ElhiTech • support@elhi.tech`}
              </Typography>
            </Box>

            {/* Copyright — desktop (absolute) */}
            <Box
              sx={{
                position: "absolute",
                bottom: "2%",
                left: 0,
                right: 0,
                display: { xs: "none", md: "flex" },
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  color: "rgb(20,38,40)",
                  fontSize: { xs: "7.5px", sm: "0.67vw" },
                  lineHeight: 1.15,
                  fontWeight: 400,
                  letterSpacing: "-0.5px",
                  fontFamily: "'Open Sans', sans-serif",
                  textAlign: "center",
                }}
                className="desktop-copyright"
              >
                {`v1.0.1 • Copyright ${new Date().getFullYear()} • Powered by ElhiTech • support@elhi.tech`}
              </Typography>
            </Box>
          </Box>

          {/* Right panel */}
          <Box
            sx={{
              flex: 1,
              height: { xs: "25%", md: "100%" },
              position: "relative",
              overflow: "hidden",
            }}
          >
            <LoginRightSide />
          </Box>
        </Box>
      )}
    </>
  );
};


export function FluidLabel({ text = "One console to manage all your Elhi Products" }) {
  const containerRef = useRef(null);
  const [fontSize, setFontSize] = useState("1rem");

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      // Text should fill ~90% of width.
      // Calibrate: character count × avg char ratio ≈ 0.52 of font size

      const charCount = text.length; // 46 chars
      // fontSize = (width * 0.90) / (charCount * 0.52)
      const computed = (width * 0.90) / (charCount * 0.52);
      // Clamp between a min and max so it never gets absurd
      const clamped = Math.min(Math.max(computed, 10), 48);
      setFontSize(`${clamped.toFixed(2)}px`);
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Typography
      ref={containerRef}
      sx={{
        color: "rgb(25,67,62)",
        textAlign: "start",
        fontSize,
        marginLeft: { xs: 0, md: "0.2vw" },
        width: "100%",
        whiteSpace: "nowrap",
        display: "block",
      }}
    >
      {text}
    </Typography>
  );
}
export default MFAHandler;
